// ─── Price Service ────────────────────────────────────────────────
// ONLY real data. No simulated or generated prices.
// Priority:
//   Normal cards: TCGPlayer > Cardmarket > eBay
//   SP/Parallel cards: eBay > TCGPlayer > Cardmarket
//   (because TCGPlayer/OPTCG API doesn't distinguish SP prices)

import { getOnePiecePriceHistory } from "./onePieceApi";

/**
 * Detect if a card is a Special Parallel (SP) or Alternate Art variant
 */
function isSpecialVariant(card) {
  if (!card) return false;
  const name = (card.name || "").toLowerCase();
  const variant = (card.variant || "").toLowerCase();
  return name.includes("(sp)") || name.includes("(parallel)") ||
    variant.includes("parallel") || variant.includes("alternate") ||
    variant.includes("alt art") || variant.includes("special");
}

/**
 * Get the latest price for a card (real data only)
 * For SP/Parallel cards, eBay is prioritized since TCGPlayer shows normal version price
 */
export function getLatestPrice(card, ebayData = null) {
  if (!card) return 0;

  const isSP = isSpecialVariant(card);

  // SP/Parallel cards: eBay first (has correct SP-specific prices)
  if (isSP && ebayData && ebayData.avg) {
    return ebayData.avg;
  }

  // SP cards without eBay data yet: return 0 (don't show wrong TCGPlayer price)
  if (isSP && card.game === "one_piece") {
    return 0; // Will update when eBay loads
  }

  // One Piece normal cards: OPTCG API market price
  if (card.game === "one_piece" && card.market_price) {
    return card.market_price;
  }

  // Pokémon: TCGPlayer prices
  if (card.tcgplayer_prices) {
    const types = ["holofoil", "reverseHolofoil", "normal", "1stEditionHolofoil", "unlimitedHolofoil"];
    for (const type of types) {
      if (card.tcgplayer_prices[type]?.market) return card.tcgplayer_prices[type].market;
      if (card.tcgplayer_prices[type]?.mid) return card.tcgplayer_prices[type].mid;
    }
  }

  // Pokémon: Cardmarket prices
  if (card.cardmarket_prices?.averageSellPrice) return card.cardmarket_prices.averageSellPrice;

  // One Piece: inventory price
  if (card.game === "one_piece" && card.inventory_price) return card.inventory_price;

  // Fallback: eBay average price
  if (ebayData && ebayData.avg) return ebayData.avg;

  return 0;
}

/**
 * Check if a card has real pricing data
 */
export function hasRealPrice(card) {
  if (!card) return false;
  if (card.game === "one_piece" && card.market_price) return true;
  if (card.tcgplayer_prices) return true;
  if (card.cardmarket_prices?.averageSellPrice) return true;
  return false;
}

/**
 * Get price source label
 */
export function getPriceSource(card, ebayData = null) {
  if (!card) return "N/A";

  const isSP = isSpecialVariant(card);

  if (isSP && ebayData && ebayData.avg) return "eBay";
  if (isSP && card.game === "one_piece") return "N/A"; // Don't show TCGPlayer for SP

  if (card.game === "one_piece" && card.market_price) return "TCGPlayer";
  if (card.tcgplayer_prices) return "TCGPlayer";
  if (card.cardmarket_prices?.averageSellPrice) return "Cardmarket";
  if (card.game === "one_piece" && card.inventory_price) return "TCGPlayer";
  if (ebayData && ebayData.avg) return "eBay";
  return "N/A";
}

/**
 * Fetch real price history for a card
 */
export async function fetchRealPriceHistory(card) {
  if (!card) return [];

  if (card.game === "one_piece" && card.api_id) {
    const history = await getOnePiecePriceHistory(card.api_id);
    return history;
  }

  return [];
}
